import { useGetBulkDialogContent } from "@components/common/faro-table/bulk-dialog/bulk-dialog-helper";
import { BulkDialogs } from "@components/common/faro-table/bulk-dialog/bulk-dialog-types";
import { BaseMemberAndTeam } from "@custom-types/member-types";
import { useAppSelector } from "@store/store-helper";
import {
  bulkActionResultsSelector,
  numberOfUpdatingItemsSelector,
} from "@store/table/table-selector";
import { nounPluralize } from "@utils/data-display";
import { isTeam } from "@utils/team-utils";
import { getPrettyName } from "@utils/user-utils";

/** Determines the content of bulk dialog and success dialog for removing a member/team from projects */
export function useRemoveFromProjectsBulkDialogContent(
  member: BaseMemberAndTeam
): BulkDialogs {
  const memberDisplayName = getPrettyName(member);
  const numberOfUpdatingItems = useAppSelector(numberOfUpdatingItemsSelector);
  const { numberOfSuccess, numberOfErrors } = useAppSelector(
    bulkActionResultsSelector
  );
  const subjectLabel = isTeam(member) ? "team" : "member";

  const pluralizedProject = nounPluralize({
    counter: numberOfUpdatingItems,
    word: "project",
    shouldShowCounter: false,
  });

  const bulkDialogContent = useGetBulkDialogContent({
    initial: {
      title: `Remove member from the ${pluralizedProject}?`,
      description:
        `When you confirm this action, the ${subjectLabel} ` +
        `${memberDisplayName} will no longer be able to access the following ${pluralizedProject}.`,
    },

    failed: {
      title: `Couldn't remove ${numberOfErrors} of ${numberOfUpdatingItems} ${pluralizedProject}`,
      description:
        "These projects couldn't be removed, check the details and try again.",
    },
    confirmButtonText: "remove",
    entityType: "project",
  });

  const pluralizedSuccessProject = nounPluralize({
    counter: numberOfSuccess,
    word: "project",
    shouldShowCounter: false,
  });

  const successDialogContent = {
    title: `${nounPluralize({
      counter: numberOfSuccess,
      word: "project",
    })} removed`,
    description: `The ${subjectLabel} ${memberDisplayName} has been removed from the selected ${pluralizedSuccessProject}.`,
  };

  return { bulkDialogContent, successDialogContent };
}
